import { useState } from 'react'
import { alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

import { useAppDispatch } from 'src/store/hooks'
import Survey from 'src/v3/components/survey'
import type { SurveyFormValues } from 'src/v3/components/survey'
import CardSectionHeader from 'src/v3/components/card-section-header'
import { fetchEventProgress } from '../events-v2.slice'
import { postEventSurveyV2 } from '../utils/api'

type SurveyCardProps = {
  eventId: string
  hours: number
  onSuccess: (msg: string) => void
  onError: (msg: string) => void
  sessionName?: string
  sessionDisplayTime?: string
  // Controlled so the Requires attention session row can open the same dialog.
  dialogOpen: boolean
  onDialogOpenChange: (open: boolean) => void
}

let SurveyCard = ({
  eventId,
  hours,
  onSuccess,
  onError,
  sessionName,
  sessionDisplayTime,
  dialogOpen,
  onDialogOpenChange: setDialogOpen,
}: SurveyCardProps) => {
  let dispatch = useAppDispatch()
  let [submitting, setSubmitting] = useState(false)

  let handleSubmit = (values: SurveyFormValues): Promise<void> => {
    setSubmitting(true)
    return postEventSurveyV2(eventId, values)
      .then(() => {
        dispatch(fetchEventProgress(eventId))
        onSuccess('Survey submitted')
        setDialogOpen(false)
      })
      .catch(() => {
        onError('Error submitting survey')
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Paper
      className="flex flex-col gap-3 p-6"
      sx={(theme) => ({
        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
        borderTop: `3px solid ${theme.palette.warning.main}`,
      })}
    >
      <CardSectionHeader label="Survey" />
      {sessionName && sessionDisplayTime && (
        <Box className="flex flex-col gap-1">
          <Typography sx={{ fontWeight: 500 }}>{sessionName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {sessionDisplayTime}
          </Typography>
        </Box>
      )}
      <Typography variant="body2" color="text.secondary">
        {`Complete the required survey to release your ${hours} CE hr${hours === 1 ? '' : 's'}.`}
      </Typography>
      <Button
        variant="contained"
        color="warning"
        fullWidth
        onClick={() => setDialogOpen(true)}
      >
        Complete Survey
      </Button>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!submitting) setDialogOpen(false)
        }}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby="survey-dialog-title"
      >
        <Box className="p-4">
          <Typography id="survey-dialog-title" variant="h6" className="mb-4">
            Event Survey
          </Typography>
          <Survey onSubmit={handleSubmit} />
        </Box>
      </Dialog>
    </Paper>
  )
}

export default SurveyCard
